/**
 * MyCoachesPage - Page for users to manage their coaches
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  UserPlus,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Settings,
  Trash2,
  Eye,
  EyeOff,
  BarChart2,
  Dumbbell,
  Loader2,
} from 'lucide-react';
import { useCoachStore, COACH_SPECIALIZATIONS } from '../stores/coachStore';
import InviteCoachModal from '../components/coach/InviteCoachModal';

const MyCoachesPage = () => {
  const navigate = useNavigate();
  const {
    myCoaches,
    isLoadingMyCoaches,
    loadMyCoaches,
    revokeCoachAccess,
    openInviteModal,
    isInviteModalOpen,
  } = useCoachStore();

  useEffect(() => {
    loadMyCoaches();
  }, [loadMyCoaches]);

  const activeCoaches = myCoaches.filter((c) => c.status === 'accepted');
  const pendingInvites = myCoaches.filter((c) => c.status === 'pending');

  const handleRevokeAccess = async (relationshipId, coachName) => {
    if (confirm(`${coachName} koçunun erişimini iptal etmek istediğinize emin misiniz?`)) {
      try {
        await revokeCoachAccess(relationshipId);
      } catch (error) {
        // Error handled in store
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full">
            <Clock className="w-3 h-3" />
            Bekliyor
          </span>
        );
      case 'accepted':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            Aktif
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full">
            <XCircle className="w-3 h-3" />
            Reddedildi
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Users className="w-6 h-6 text-orange-500" />
                  Koçlarım
                </h1>
                <p className="text-sm text-gray-400">
                  Koçlarınızı yönetin ve yeni koç davet edin
                </p>
              </div>
            </div>
            <motion.button
              onClick={() => openInviteModal()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Koç Davet Et
            </motion.button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-8">
        {isLoadingMyCoaches ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : (
          <>
            {/* Active Coaches */}
            <section>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Aktif Koçlar
                {activeCoaches.length > 0 && (
                  <span className="text-sm text-gray-400">({activeCoaches.length})</span>
                )}
              </h2>

              {activeCoaches.length === 0 ? (
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 text-center">
                  <Users className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4">Henüz aktif koçunuz yok</p>
                  <button
                    onClick={() => openInviteModal()}
                    className="text-orange-400 hover:text-orange-300 text-sm font-medium"
                  >
                    Koç davet et
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeCoaches.map((relationship) => (
                    <CoachCard
                      key={relationship.id}
                      relationship={relationship}
                      onRevoke={() =>
                        handleRevokeAccess(
                          relationship.id,
                          relationship.coach_info?.display_name
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Pending Invitations */}
            {pendingInvites.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  Bekleyen Davetler
                  <span className="text-sm text-gray-400">({pendingInvites.length})</span>
                </h2>

                <div className="space-y-4">
                  {pendingInvites.map((relationship) => (
                    <div
                      key={relationship.id}
                      className="bg-gray-900/50 border border-gray-800 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-yellow-500/10 rounded-full flex items-center justify-center">
                            <Dumbbell className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {relationship.coach_info?.display_name || 'Koç'}
                            </p>
                            <p className="text-sm text-gray-400">
                              Davet bekleniyor...
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(relationship.status)}
                          <button
                            onClick={() =>
                              handleRevokeAccess(
                                relationship.id,
                                relationship.coach_info?.display_name
                              )
                            }
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Daveti iptal et"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Invite Modal */}
      <InviteCoachModal />
    </div>
  );
};

// Coach Card Component
const CoachCard = ({ relationship, onRevoke }) => {
  const coachInfo = relationship.coach_info;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900/50 border border-gray-800 rounded-xl p-5"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-12 h-12 bg-orange-500/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Dumbbell className="w-6 h-6 text-orange-400" />
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold">{coachInfo?.display_name || 'Koç'}</h3>
              {coachInfo?.has_profile && (
                <span className="text-xs text-orange-400">Koç Profili</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-2">{coachInfo?.email}</p>

            {/* Specializations */}
            {coachInfo?.specializations?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {coachInfo.specializations.map((spec) => (
                  <span
                    key={spec}
                    className="px-2 py-0.5 bg-gray-800 text-gray-300 text-xs rounded"
                  >
                    {COACH_SPECIALIZATIONS[spec]?.emoji}{' '}
                    {COACH_SPECIALIZATIONS[spec]?.label || spec}
                  </span>
                ))}
              </div>
            )}

            {/* Permissions */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                {relationship.can_view_workouts ? (
                  <Eye className="w-3 h-3 text-green-400" />
                ) : (
                  <EyeOff className="w-3 h-3 text-gray-600" />
                )}
                Antrenmanlar
              </span>
              <span className="flex items-center gap-1">
                {relationship.can_view_analytics ? (
                  <BarChart2 className="w-3 h-3 text-green-400" />
                ) : (
                  <BarChart2 className="w-3 h-3 text-gray-600" />
                )}
                Analizler
              </span>
            </div>

            {/* Bio */}
            {coachInfo?.bio && (
              <p className="text-sm text-gray-400 mt-2 line-clamp-2">{coachInfo.bio}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title="Ayarlar"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={onRevoke}
            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Erişimi İptal Et"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Accepted Date */}
      <div className="mt-4 pt-3 border-t border-gray-800 text-xs text-gray-500">
        Kabul tarihi:{' '}
        {new Date(relationship.accepted_at).toLocaleDateString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </div>
    </motion.div>
  );
};

export default MyCoachesPage;
